/** Libellés et styles des statuts du pipeline (UI admin). */

import type {
  PipelineRunStatus,
  PipelineScenarioStatus,
  PipelineMatchStatus,
} from '@jeraisonne/db';

export const RUN_STATUS_LABEL: Record<PipelineRunStatus, string> = {
  scenarios_generating: 'Génération des scénarios',
  scenarios_auditing: 'Audit des scénarios',
  awaiting_scenario_review: 'Scénarios à valider',
  matching: 'Matching en cours',
  awaiting_match_selection: 'Matchings à valider',
  generating: 'Génération des exercices',
  completed: 'Terminé',
  failed: 'Échec',
  cancelled: 'Annulé',
};

/** Classe Tailwind du badge de statut (bordure + fond + texte). */
export const RUN_STATUS_CLS: Record<PipelineRunStatus, string> = {
  scenarios_generating: 'bg-primary-50 text-primary-700 border-primary-200',
  scenarios_auditing: 'bg-primary-50 text-primary-700 border-primary-200',
  awaiting_scenario_review: 'bg-amber-50 text-amber-700 border-amber-200',
  matching: 'bg-primary-50 text-primary-700 border-primary-200',
  awaiting_match_selection: 'bg-amber-50 text-amber-700 border-amber-200',
  generating: 'bg-primary-50 text-primary-700 border-primary-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  failed: 'bg-accent-50 text-accent-700 border-accent-200',
  cancelled: 'bg-ink-100 text-ink-500 border-ink-200',
};

/** Un run dont l'admin doit s'occuper (point de validation humaine ouvert). */
export function runNeedsAttention(status: PipelineRunStatus): boolean {
  return status === 'awaiting_scenario_review' || status === 'awaiting_match_selection';
}

/** Un run encore actif (ni terminé, ni échoué, ni annulé). */
export function runIsActive(status: PipelineRunStatus): boolean {
  return status !== 'completed' && status !== 'failed' && status !== 'cancelled';
}

/**
 * Libellés des phases du worker `pipeline-process-job` — valeurs possibles de
 * `pipeline_exercise_jobs.current_step`. Sert à indiquer à quelle étape précise
 * un job a échoué / abandonné.
 */
export const JOB_STEP_LABEL: Record<string, string> = {
  generating_statement: 'Génération de l’énoncé',
  verifying_statement: 'Vérification de l’énoncé',
  generating_solution: 'Génération du corrigé',
  verifying_solution: 'Vérification du corrigé',
  generating_distractors: 'Génération des distracteurs',
  finalizing: 'Assemblage de l’exercice',
};

/** Libellé lisible d'une phase de job (`current_step`) — repli sur la valeur brute. */
export function jobStepLabel(step: string | null | undefined): string | null {
  if (!step) return null;
  return JOB_STEP_LABEL[step] ?? step;
}

export const SCENARIO_STATUS_LABEL: Record<PipelineScenarioStatus, string> = {
  pending_review: 'À valider',
  validated: 'Validé',
  discarded: 'Supprimé',
  regenerating: 'Régénération…',
};

export const SCENARIO_STATUS_CLS: Record<PipelineScenarioStatus, string> = {
  pending_review: 'bg-amber-50 text-amber-700 border-amber-200',
  validated: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  discarded: 'bg-ink-100 text-ink-500 border-ink-200',
  regenerating: 'bg-primary-50 text-primary-700 border-primary-200',
};

export const MATCH_STATUS_LABEL: Record<PipelineMatchStatus, string> = {
  proposed: 'Proposé',
  selected: 'Sélectionné',
  discarded: 'Exclu',
  auto_discarded: 'Auto-rejeté',
};

export const DIFFICULTY_LABEL: Record<'easy' | 'medium' | 'hard', string> = {
  easy: 'Facile',
  medium: 'Moyen',
  hard: 'Difficile',
};
